import { useState, FormEvent } from 'react';
import { X } from 'lucide-react';
import type { Widget } from '../types';

interface WidgetFormProps {
  widget?: Widget;
  organizationId: string;
  onSubmit: (data: any) => Promise<void>;
  onClose: () => void;
}

export default function WidgetForm({ widget, organizationId, onSubmit, onClose }: WidgetFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: widget?.name || '',
    websiteUrl: widget?.websiteUrl || '',
    primaryColor: widget?.widgetConfig?.theme?.primaryColor || '#3B82F6',
    position: widget?.widgetConfig?.theme?.position || 'bottom-right',
    greeting: widget?.widgetConfig?.greeting || 'Hi! How can I help you today?',
    placeholder: widget?.widgetConfig?.placeholder || 'Type your message...',
    showBranding: widget?.widgetConfig?.showBranding ?? true,
    model: widget?.aiConfig?.model || '@cf/meta/llama-3.1-8b-instruct',
    temperature: widget?.aiConfig?.temperature || 0.7,
    maxTokens: widget?.aiConfig?.maxTokens || 512,
    systemPrompt: widget?.aiConfig?.systemPrompt || 'You are a helpful AI assistant.',
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = {
        name: formData.name,
        websiteUrl: formData.websiteUrl,
        organizationId,
        widgetConfig: {
          theme: {
            primaryColor: formData.primaryColor,
            position: formData.position as any,
          },
          greeting: formData.greeting,
          placeholder: formData.placeholder,
          showBranding: formData.showBranding,
        },
        aiConfig: {
          model: formData.model,
          temperature: formData.temperature,
          maxTokens: formData.maxTokens,
          systemPrompt: formData.systemPrompt,
        },
      };

      await onSubmit(data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save widget');
    } finally {
      setLoading(false);
    }
  };

  const availableModels = [
    { value: '@cf/meta/llama-3.1-8b-instruct', label: 'Llama 3.1 8B (Fast)' },
    { value: '@cf/meta/llama-3.1-70b-instruct', label: 'Llama 3.1 70B (Smart)' },
    { value: '@cf/mistral/mistral-7b-instruct-v0.1', label: 'Mistral 7B' },
    { value: '@cf/qwen/qwen1.5-7b-chat-awq', label: 'Qwen 1.5 7B' },
  ];

  const positions = [
    { value: 'bottom-right', label: 'Bottom Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'top-left', label: 'Top Left' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {widget ? 'Edit Widget' : 'Create Widget'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Widget Name *
              </label>
              <input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input mt-1"
                placeholder="My Support Widget"
              />
            </div>

            <div>
              <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700">
                Website URL *
              </label>
              <input
                id="websiteUrl"
                type="url"
                required
                value={formData.websiteUrl}
                onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                className="input mt-1"
                placeholder="https://yourwebsite.com"
              />
            </div>
          </div>

          {/* Appearance */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900">Appearance</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700">
                  Primary Color
                </label>
                <input
                  id="primaryColor"
                  type="color"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  className="mt-1 h-10 w-full rounded border border-gray-300"
                />
              </div>

              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                  Position
                </label>
                <select
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value as any })}
                  className="input mt-1"
                >
                  {positions.map((pos) => (
                    <option key={pos.value} value={pos.value}>
                      {pos.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="greeting" className="block text-sm font-medium text-gray-700">
                Greeting Message
              </label>
              <input
                id="greeting"
                type="text"
                value={formData.greeting}
                onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                className="input mt-1"
                placeholder="Hi! How can I help you today?"
              />
            </div>

            <div>
              <label htmlFor="placeholder" className="block text-sm font-medium text-gray-700">
                Input Placeholder
              </label>
              <input
                id="placeholder"
                type="text"
                value={formData.placeholder}
                onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                className="input mt-1"
                placeholder="Type your message..."
              />
            </div>

            <div className="flex items-center">
              <input
                id="showBranding"
                type="checkbox"
                checked={formData.showBranding}
                onChange={(e) => setFormData({ ...formData, showBranding: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="showBranding" className="ml-2 text-sm text-gray-700">
                Show "Powered by NorthCoast.ai" branding
              </label>
            </div>
          </div>

          {/* AI Configuration */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900">AI Configuration</h3>

            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-700">
                AI Model
              </label>
              <select
                id="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="input mt-1"
              >
                {availableModels.map((model) => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="temperature" className="block text-sm font-medium text-gray-700">
                  Temperature ({formData.temperature})
                </label>
                <input
                  id="temperature"
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                  className="mt-1 w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lower = more focused, Higher = more creative
                </p>
              </div>

              <div>
                <label htmlFor="maxTokens" className="block text-sm font-medium text-gray-700">
                  Max Tokens
                </label>
                <input
                  id="maxTokens"
                  type="number"
                  min="128"
                  max="2048"
                  step="128"
                  value={formData.maxTokens}
                  onChange={(e) => setFormData({ ...formData, maxTokens: parseInt(e.target.value) })}
                  className="input mt-1"
                />
              </div>
            </div>

            <div>
              <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-700">
                System Prompt
              </label>
              <textarea
                id="systemPrompt"
                rows={4}
                value={formData.systemPrompt}
                onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                className="input mt-1"
                placeholder="You are a helpful AI assistant..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Define the AI's personality and role
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 border-t pt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary px-4 py-2"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary px-6 py-2"
              disabled={loading}
            >
              {loading ? 'Saving...' : widget ? 'Save Changes' : 'Create Widget'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
